import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import { Transition } from 'react-transition-group';
import {useSearchParams} from 'react-router-dom';
import TableFilters from '../../components/pages/TransactionsPage/TableFilters';
import TransactionsTable from '../../components/pages/TransactionsPage/TransactionsTable';
import LoadMoreButton from '../../components/pages/TransactionsPage/LoadMoreButton';
import {
  StyledTransactionsPageContainer,
  StyledTransactionsPageContent,
} from './StyledTransactionsPage';
import PageContainer from '../../components/PageContainer';
import TransactionDetailsPanel from '../../components/pages/TransactionsPage/TransactionDetailsPanel';
import TransactionDetails from '../../components/pages/TransactionsPage/TransactionDetails';
import TransactionsPageHeaderWrapper from '../../components/pages/TransactionsPage/TransactionsPageHeaderWrapper';
import NoSearchResults from '../../components/NoSearchResults';
import Empty from '../../components/Empty';
import AuthenticationWindow from '../../components/pages/CardsPage/AuthenticationWindow';
import {errorHandler} from '../../state/services/request';
import {expenseActions} from '../../state/actions/expense.actions';
import {invoicesActions} from '../../state/actions/invoice.actions';
import {subscriptionActions} from '../../state/actions/subscription.actions';
import {transactionActions} from '../../state/actions/transaction.actions';
import {ReactComponent as EmptySvgImage} from '../../static/images/pages/transactions/empty-transactions.svg';
import {SCAActionsConstants, SYSTEM_ANIMATION_DURATION} from '../../constants';
import {
  dateHelpers,
  elementHelpers,
  objectHelpers,
  scaHelpers,
  subscriptionsHelpers,
  systemHelpers,
  transactionsHelpers
} from '../../utils/helpers';
import {firebaseEvents} from '../../snippets/firebase';
import {useCompanyBankingCreationDate, useHasAdminRights} from '../../hooks';
import {INITIAL_LOADED_TRANSACTION_MAX_COUNT, TRANSACTION_TABLE_ELEMENT_ID, TRANSACTION_PAGE_SEARCH_KEYS} from './transactionPage.constants';

const gObjProp = objectHelpers.getObjProp;

const {details: detailsStyles, table: tableStyles} = transactionsHelpers.getAnimationStyles({duration: SYSTEM_ANIMATION_DURATION});

const {SELECTED_TRANSACTION_ID, SELECTED_TRANSACTION_DATE} = TRANSACTION_PAGE_SEARCH_KEYS;

const {TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION, TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION} = SCAActionsConstants;

const TransactionsPage = ({
  getBatchExpenses,
  getSubscription,
  getTransactionsList,
  getUserTransactionsList,
  getTransactionsTotals,
  unlinkExpense,
  isAdmin,
  employeeId,
  employees,
}) => {
  const [t] = useTranslation(['main', 'transactions']);
  const [loading, setLoading] = useState(true);
  const [groupedTransactions, setGroupedTransactions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totals, setTotals] = useState([]);
  const [loadMoreProps, setLoadMoreProps] = useState({
    cursor: undefined,
    loading: false,
    months: 1
  });
  const [filterParams, setFilterParams] = useState({});
  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [authWindowProps, setAuthWindowProps] = useState({open: false});
  const [activeOperationName, setActiveOperationName] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(undefined);
  const [isFixedDetailsWindow, setIsFixedDetailsWindow] = useState(false);
  const [detailsWindowFixedHeight, setDetailsWindowFixedHeight] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const hasAdminRights = useHasAdminRights();

  const companyBankingCreationDate = useCompanyBankingCreationDate();

  const tableNodeRef = useRef(null);
  const detailsNodeRef = useRef(null);

  const initialLoadedMonthsCount = useMemo(() => {
    const DEFAULT_MONTHS = 3;
    const selectedTransactionDate = searchParams.get(SELECTED_TRANSACTION_DATE);

    if (!selectedTransactionDate) return DEFAULT_MONTHS;

    const monthsCount = dateHelpers.getFullMonthsFromToday(selectedTransactionDate);

    return Math.max(monthsCount, DEFAULT_MONTHS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isEnabledLoadMore = useMemo(() => {
    const { cursor, months } = loadMoreProps || {};
    const isEmptyQuery = objectHelpers.isEmptyQuery(filterParams);

    return isEmptyQuery ? subscriptionsHelpers.checkIsEnabledLoadMore(companyBankingCreationDate, months) : Boolean(cursor);
  }, [loadMoreProps, companyBankingCreationDate, filterParams]);

  const selectedSubscription = useMemo(() => {
    let subscription;
    const expense = gObjProp(selectedTransaction, 'expense');
    const subscriptionId = gObjProp(expense, 'subscription_id');
    if (isOpenDetails && subscriptionId) subscription = subscriptions.find(s => s.id === subscriptionId);
    return subscription;
  }, [subscriptions, isOpenDetails, selectedTransaction]);

  const emptyT = (key) => t(`transactions:empty.${key}`);

  useEffect(() => {
    if (!isAdmin) {
      let offsetTop = elementHelpers.getElementOffsetTop(`#${TRANSACTION_TABLE_ELEMENT_ID}`, 4) - 85;
      if (offsetTop !== detailsWindowFixedHeight) setDetailsWindowFixedHeight(offsetTop);
    }
  }, [isFixedDetailsWindow]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAdmin) {
      transactionsHelpers.addFixedDetailsWindowScrollEventListener({
        detailsWindowFixedHeight, isOpenDetails, isFixedDetailsWindow, setIsFixedDetailsWindow
      });
    }
  }, [isOpenDetails, isFixedDetailsWindow, detailsWindowFixedHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const transactionsWithExpense = transactionsHelpers.getTransactionsWithExpense({expenses, employees, transactions});
    let groupedTransactions = transactionsHelpers.getGroupedTransactions(transactionsWithExpense, totals);
    setGroupedTransactions(groupedTransactions);
  }, [transactions, expenses, employees, totals]);

  useEffect(() => {
    if (selectedTransaction !== null) return;

    const selectedTransactionId = searchParams.get(SELECTED_TRANSACTION_ID);
    if (!selectedTransactionId) return;

    const transaction = groupedTransactions.find(t => t.expense_id === selectedTransactionId);
    if (transaction?.expense) {
      handleOpenDetails(transaction.id);
    }
  }, [groupedTransactions]); // eslint-disable-line react-hooks/exhaustive-deps

  const {isEmptySearchTransactions, isNotExistsTransactions} = useMemo(() => ({
    isEmptySearchTransactions: Boolean(filterParams?.search) && transactions.length === 0 && !loading,
    isNotExistsTransactions: !isEnabledLoadMore && !loading && transactions.length === 0 && objectHelpers.isEmptyQuery(filterParams)
  }), [filterParams, isEnabledLoadMore, loading, transactions]);

  const getLoadMoreTransactionsSuccessCallback = (response) => {
    getTransactionsSuccessCallback({
      response,
      previousExpenses: expenses,
      previousTransactions: transactions
    });

    if (!Boolean(response?.pagination)) {
      const subtractMonthsCount = loadMoreProps.months + 1;
      setLoadMoreProps({
        ...loadMoreProps,
        cursor: undefined,
        loading: false,
        months: subtractMonthsCount,
      });
    }
  }

  const getTransactionsSuccessCallback = ({response, previousExpenses = [], previousTransactions = []} = {}) => {
    const transactions = response.hasOwnProperty('results') ? response.results : response || [];
    const expensesIds = transactions.map(t => t.expense_id).filter(value => value);
    const isExistsExpensesIds = expensesIds.length > 0;

    setTransactions([...previousTransactions, ...transactions]);
    setLoading(false);

    if (response.pagination) {
      const cursor = response?.pagination?.next_cursor;
      setLoadMoreProps({
        ...loadMoreProps,
        cursor,
      });
    }

    if (isExistsExpensesIds) {
      getBatchExpenses({
        data: {expenses_ids: expensesIds},
        successCallback: (expenses) => setExpenses([...previousExpenses, ...expenses])
      });
    } else if (!isExistsExpensesIds && expenses.length > 0) {
      setExpenses([]);
    }
  }

  const handleOnFilterChange = (query) => {
    query = objectHelpers.omitInvalidValues({
      ...objectHelpers.getSearchParams(searchParams),
      ...query
    });
    setSearchParams(query);

    query = removeSelectedKeysFromObject(query);
    setFilterParams(query);

    !loading && setLoading(true);

    getTransactionsListPromises({
      operationName: TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION,
      query,
      successCallback: (response) => getTransactionsSuccessCallback({response}),
      errorCallback: () => setLoading(false)
    });
  }

  const getTransactionsListPromises = ({
    query,
    successCallback,
    errorCallback,
    subtractMonthsCount,
    operationName
  }) => {
    const disableMonthSelection = !objectHelpers.isEmptyQuery(query) || (query.hasOwnProperty('has_invoice') && typeof query.has_invoice === 'boolean');
    const errorFunc = (response) => errorHandler(response, errorCallback);
    const transactionRequest = (subtractMonthsCount, successFunc, successTotalsFunc) => {
      const requestQuery = subscriptionsHelpers.getTransactionRequestQuery(query, subtractMonthsCount, disableMonthSelection);
      const getTransactionsFunc = isAdmin ? getTransactionsList : getUserTransactionsList;

      // load transactions list
      getTransactionsFunc({
        headers: scaHelpers.getAuthHeaders(operationName || activeOperationName),
        userId: employeeId,
        query: requestQuery,
        successCallback: successFunc,
        errorCallback: (response) => {
          scaHelpers.SCAResponseCallback({
            response,
            scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
            errorCallback: errorFunc
          });
        }
      });

      // load totals only if request query has month and year params
      if (isAdmin && requestQuery.hasOwnProperty('month') && requestQuery.hasOwnProperty('year')) {
        getTransactionsTotals({
          headers: scaHelpers.getAuthHeaders(operationName || activeOperationName),
          query: requestQuery,
          successCallback: successTotalsFunc
        });
      } else {
        setTotals([]);
      }
    }

    let storedTransactions = [];
    let storedTotals = [];

    activeOperationName !== operationName && setActiveOperationName(operationName);

    if (subtractMonthsCount || disableMonthSelection) {
      transactionRequest(
        subtractMonthsCount,
        (response) => successCallback && successCallback(response),
        (response) => setTotals([...totals, response])
      );
    } else {
      const loadTransactions = (index, maxLength) => {
        const successFunc = (data) => {
          storedTransactions = [...storedTransactions, ...data];
          let nextIndex = index + 1;
          if (loadMoreProps.months !== nextIndex) {
            setLoadMoreProps({
              ...loadMoreProps,
              cursor: undefined,
              months: nextIndex,
            });
          }
          if (nextIndex < maxLength && storedTransactions.length <= INITIAL_LOADED_TRANSACTION_MAX_COUNT) {
            setTransactions(storedTransactions);
            loadTransactions(nextIndex, maxLength);
          } else {
            successCallback && successCallback(storedTransactions);
          }
        }
        const successTotalsFunc = (data) => {
          storedTotals = [...storedTotals, data];
          setTotals(storedTotals);
        }
        transactionRequest(index, successFunc, successTotalsFunc);
      }

      loadTransactions(0, initialLoadedMonthsCount);
    }
  }

  const handleLoadMore = () => {
    if (isEnabledLoadMore) {
      const {cursor, months} = loadMoreProps;
      const subtractMonthsCount = months + 1;
      let updateLoadMoreProps = {
        ...loadMoreProps,
        loading: true
      }
      const enabledPagination = Boolean(cursor);
      if (!enabledPagination) {
        updateLoadMoreProps = {
          ...updateLoadMoreProps,
          cursor: undefined,
          months: subtractMonthsCount,
        }
      }
      setLoadMoreProps({...loadMoreProps, loading: true});

      getTransactionsListPromises({
        operationName: TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION,
        query: {...filterParams, cursor},
        successCallback: getLoadMoreTransactionsSuccessCallback,
        errorCallback: () => {
          setLoading(false);
          setLoadMoreProps({...updateLoadMoreProps, loading: false});
        },
        subtractMonthsCount: months,
      });
    }
  }

  const onExpenseUpdate = (updatedExpense) => {
    if (updatedExpense) {
      let selectedTransactionExpense = {...selectedTransaction.expense};
      // update expenses list
      setExpenses(
        transactionsHelpers.getUpdatedBatchExpensesList(expenses, updatedExpense)
      );

      // update selected transaction if the transaction details window is open
      if (selectedTransaction) {
        setSelectedTransaction({
          ...selectedTransaction,
          expense: {
            ...selectedTransactionExpense,
            ...updatedExpense
          }
        });
      }

      // remove or add transaction to table if unmatched filter is selected
      if (
        String(filterParams.has_invoice) === 'false' &&
        selectedTransactionExpense.expense_type !== updatedExpense.expense_type
      ) {
        const isExpenseTypeAdded = selectedTransactionExpense.expense_type === null && updatedExpense.expense_type !== null;
        const isExpenseTypeRemoved = selectedTransactionExpense.expense_type !== null && updatedExpense.expense_type === null;
        if (isExpenseTypeAdded) {
          setTransactions(
            transactions.filter(transaction => transaction.id !== selectedTransaction?.id)
          );
        } else if (isExpenseTypeRemoved) {
          const isAlreadyExistTransaction = transactions.find(t => t.id === selectedTransaction.id);
          if (!isAlreadyExistTransaction) {
            setTransactions(
              [...transactions, selectedTransaction]
                .sort((a, b) => new Date(b.created_date) - new Date(a.created_date))
            );
          }
        }
      }
    }
  }

  const successChangeInvoiceHandle = (response, successCallback) => {
    successCallback && successCallback(response);
    systemHelpers.logEvent(firebaseEvents.TRANSACTIONS_UPDATE_TRANSACTION_INVOICE);
  }

  const handleInvoiceRemove = (successCallback, errorCallback) => {
    transactionsHelpers.removeInvoiceFromTransaction({
      t,
      transaction: selectedTransaction,
      successCallback: (response) => successChangeInvoiceHandle(response, successCallback),
      errorCallback,
      unlinkExpense,
      onExpenseUpdate
    });
  }

  const removeSelectedKeysFromObject = (obj) =>
    objectHelpers.removeKeysFromObject(obj, [SELECTED_TRANSACTION_ID, SELECTED_TRANSACTION_DATE])

  const handleCloseDetails = () => {
    setIsOpenDetails(false);
    if (selectedRowIndex) setSelectedRowIndex(undefined);

    const updatedSearchParams = removeSelectedKeysFromObject(objectHelpers.getSearchParams(searchParams));
    setSearchParams(updatedSearchParams);
  }

  const handleOpenDetails = (transactionId, index) => {
    let subscriptionId = null;
    let transaction = groupedTransactions.find(d => d.id === transactionId);
    if (transaction) {
      const expenseId = transaction.expense_id;
      const expense = expenses.find(e => e.id === expenseId);
      if (expense) {
        subscriptionId = expense.subscription_id;
        transaction = {...transaction, expense: {...transaction.expense, ...expense}};
      }
      setSearchParams({
        ...objectHelpers.getSearchParams(searchParams),
        [SELECTED_TRANSACTION_ID]: expenseId,
        [SELECTED_TRANSACTION_DATE]: dateHelpers.getDateWithMonth(transaction.created_date, 'YYYY-MM-DD')
      });
    }
    if (subscriptionId) {
      const subscription = subscriptions.find(s => s.id === subscriptionId);
      if (subscription === undefined) {
        getSubscription(
          subscriptionId,
          (subscription) => setSubscriptions([...subscriptions, subscription])
        );
      }
    }
    setSelectedTransaction(transaction);
    setIsOpenDetails(true);
    selectedRowIndex !== index && setSelectedRowIndex(index);
  }

  const handleCloseAuthModal = () => {
    setAuthWindowProps({...authWindowProps, open: false});
    loading && setLoading(false);
    if (activeOperationName === TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION) {
      setLoadMoreProps({...loadMoreProps, loading: false});
    }
  }

  const handleOnSuccessAuth = () => {
    handleCloseAuthModal();
    if (activeOperationName === TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION) {
      setLoading(true);
      handleOnFilterChange(filterParams);
    } else {
      handleLoadMore();
    }
  }

  return (
    <PageContainer>
      <TransactionsPageHeaderWrapper />
      <StyledTransactionsPageContainer className={isOpenDetails && 'open-details'}>
        {isNotExistsTransactions ? (
          <Empty
            description={emptyT('description')}
            image={<EmptySvgImage />}
            title={emptyT('title')}
          />
        ) : (
          <>
            <Transition nodeRef={tableNodeRef} in={isOpenDetails} timeout={SYSTEM_ANIMATION_DURATION}>
              {state => (
                <StyledTransactionsPageContent
                  className={!isAdmin && 'with-invoice-forward'}
                  id={TRANSACTION_TABLE_ELEMENT_ID}
                  style={{
                    ...tableStyles.default,
                    ...tableStyles[state]
                  }}
                >
                  <TableFilters
                    onFilter={handleOnFilterChange}
                    searchParams={searchParams}
                  />
                  {isEmptySearchTransactions ? (
                    <NoSearchResults
                      spinning={loading}
                      value={filterParams?.search || ''}
                    />
                  ) : (
                    <>
                      <TransactionsTable
                        data={groupedTransactions}
                        emptyMessage={loading ? ' ' : undefined}
                        isCollapsed={isOpenDetails}
                        loading={loading}
                        onRowClick={handleOpenDetails}
                        selectedRowIndex={selectedRowIndex}
                      />

                      <LoadMoreButton
                        isEnabled={isEnabledLoadMore}
                        loading={loading || loadMoreProps.loading}
                        modalTitle={t('showMoreTransactions')}
                        onClick={handleLoadMore}
                        size='large'
                      >
                        {t('showMore')}
                      </LoadMoreButton>
                    </>
                  )}
                </StyledTransactionsPageContent>
              )}
            </Transition>
            <Transition nodeRef={detailsNodeRef} in={isOpenDetails} timeout={SYSTEM_ANIMATION_DURATION}>
              {state => (
                <TransactionDetailsPanel
                  className='transaction-details-panel'
                  contentClassName='w-100 transaction-details-panel-content'
                  onBack={handleCloseDetails}
                  fixed={isAdmin ? true : isFixedDetailsWindow}
                  hiddenOverflow={!isOpenDetails}
                  headerRightContent={transactionsHelpers.getTransactionDetailsRightContent({ transaction: selectedTransaction, t })}
                  style={{
                    ...detailsStyles.default,
                    ...detailsStyles[state]
                  }}
                >
                  <TransactionDetails
                    edit={hasAdminRights}
                    onExpenseUpdate={onExpenseUpdate}
                    onInvoiceRemove={handleInvoiceRemove}
                    subscription={selectedSubscription}
                    transaction={selectedTransaction}
                  />
                </TransactionDetailsPanel>
              )}
            </Transition>
          </>
        )}

        <AuthenticationWindow
          {...authWindowProps}
          authModalProps={{title: `${t('transactions')} ${t('loading')}`}}
          handleCancel={handleCloseAuthModal}
          onSuccess={handleOnSuccessAuth}
          operationName={activeOperationName}
        />

      </StyledTransactionsPageContainer>
    </PageContainer>
  );
}

const mapStateToProps = state => {
  const {employees} = state.company;
  const {user} = state;
  const isAdmin = user.isAdmin;
  const employeeId = user?.employee?.id;

  return {
    isAdmin,
    employeeId,
    employees
  }
}

const mapDispatchToProps = {
  getBatchExpenses: expenseActions.getBatchExpenses,
  getTransactionsList: transactionActions.getTransactionsList,
  getUserTransactionsList: transactionActions.getUserTransactionsList,
  getTransactionsTotals: transactionActions.getTransactionsTotals,
  getSubscription: subscriptionActions.getSubscription,
  unlinkExpense: invoicesActions.unlinkExpense,
}

export default connect(mapStateToProps, mapDispatchToProps)(TransactionsPage);
